import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languages } from '@/lib/i18n';
import { getPublicSiteUrl, joinSitePath, stripHtmlForMeta } from '@/lib/seo';
import { buildPathForLocale, getLocaleFromPathname } from '@/lib/intl-seo';
import { renderSocialMetaTags } from '@/components/seo/SocialMetaTags';

const SUPPORTED = new Set(languages.map((l) => l.code));
const BRAND = 'Turkish E-Market';

function withBrand(title) {
  const clean = String(title || '').replace(/\s+/g, ' ').trim();
  if (!clean) return BRAND;
  if (clean.toLowerCase().includes('turkish e-market')) return clean;
  return `${clean} | ${BRAND}`;
}

/**
 * Herkese açık sayfalar için title, description, canonical ve Open Graph.
 * segments: İngilizce yol parçaları (buildPathForLocale). Örn. ['restaurants'].
 */
export function PageSeo({ title, description, segments = [], image, type = 'website', jsonLd }) {
  const { lang } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();

  const langCode = useMemo(() => {
    if (lang && SUPPORTED.has(lang)) return lang;
    return getLocaleFromPathname(location.pathname) || (i18n.language || 'en').substring(0, 2);
  }, [lang, location.pathname, i18n.language]);

  const site = getPublicSiteUrl();
  const canonical = joinSitePath(site, buildPathForLocale(langCode, segments));
  const fullTitle = withBrand(title);
  const desc = stripHtmlForMeta(description, 158);
  const ogImage = image || (site ? `${site.replace(/\/$/, '')}/logo.png` : '');
  const ld = !jsonLd ? '' : typeof jsonLd === 'string' ? jsonLd : JSON.stringify(jsonLd);

  return (
    <Helmet htmlAttributes={{ lang: langCode }}>
      <title>{fullTitle}</title>
      {desc ? <meta name="description" content={desc} /> : null}
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      {renderSocialMetaTags({
        title: fullTitle,
        description: desc,
        url: canonical,
        image: ogImage,
        type,
        locale: langCode,
      })}
      {ld ? <script type="application/ld+json">{ld}</script> : null}
    </Helmet>
  );
}
